import React from 'react';
import RegisterInterest from '../../components/modals/RegisterInterest/RegisterInterest';
import LightPillar from '../../components/LightPillar/LightPillar';

const Default: React.FC = () => {
  return (
    <>
      {/* Hero Section - Full Viewport */}
      <section className="hero-landing">
        <LightPillar
          topColor="#29e6ff"
          bottomColor="#9effcd"
          mixBlendMode="lighten"
          rotationSpeed={0.2}
          glowAmount={0.006}
        />
        <div className="hero-content">
          <h1 className="hero-title">
            <span className="hero-highlight">From Clicking to Fixing</span>
            <span className="hero-tagline">Voyage Calculations Built for Pre-Fix</span>
          </h1>
          <p className="hero-subtitle">
            VoyCalc has one purpose: make the commercial team's work simpler. Fewer clicks,
            automation wherever possible — and AI that turns what you say into a finished
            estimate.
          </p>
          <RegisterInterest />
        </div>
        <div className="scroll-indicator">
          <i className="bi bi-chevron-down"></i>
        </div>
      </section>

      {/* Positioning: who VoyCalc is for — and that it is not a VMS */}
      <section className="fit-section" aria-labelledby="fit-title">
        <div className="fit-container">
          <h2 id="fit-title" className="fit-title">
            Built for the commercial team. <span className="fit-not">Not a VMS.</span>
          </h2>
          <div className="fit-grid">
            <div className="fit-card">
              <div className="fit-card-icon">
                <i className="bi bi-people-fill"></i>
              </div>
              <h3>Who it is for</h3>
              <p>
                Chartering managers, brokers and commercial teams — everyone who works a cargo
                before it is fixed. The estimate, the options, the quote, the fixture: that is
                the whole of VoyCalc.
              </p>
            </div>
            <div className="fit-card">
              <div className="fit-card-icon fit-card-icon-outline">
                <i className="bi bi-slash-circle"></i>
              </div>
              <h3>What it is not</h3>
              <p>
                VoyCalc is not a Voyage Management System. No post-fixture voyage accounting,
                invoicing, claims or laytime settlement lives here. What VoyCalc does support is
                a proper hand-over: once the cargo is fixed, the voyage goes to operations with
                the estimate, the notes and the documents in one structured package — and
                operations carry on in the tools they already use.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* AI Agents / MCP Section */}
      <section className="ai-section">
        <div className="ai-container">
          <div className="ai-grid">
            <div className="ai-copy">
              <span className="ai-badge">Claude + MCP</span>
              <h2 className="ai-title">Let your AI agent do the clicking</h2>
              <p className="ai-shift">
                <span className="ai-shift-from">From spending your time clicking through a system</span>
                <span className="ai-shift-to">
                  to having a conversation about the opportunity you're calculating.
                </span>
              </p>
              <p className="ai-text">
                Connect Claude — or any MCP-enabled agent — directly to your VoyCalc workspace.
                Your agent works with the same data you do: estimates, vessels, ports and costs.
              </p>
              <ul className="ai-list">
                <li>
                  <i className="bi bi-check-circle-fill"></i>
                  <span>Generate complete estimates from a plain-language request or a recap e-mail</span>
                </li>
                <li>
                  <i className="bi bi-check-circle-fill"></i>
                  <span>Add and maintain vessels in your vessel database</span>
                </li>
                <li>
                  <i className="bi bi-check-circle-fill"></i>
                  <span>Build options and compare routings, bunkers and laycans side by side</span>
                </li>
              </ul>
              <div className="ai-stat">
                <span className="ai-stat-number">40–70%</span>
                <span className="ai-stat-label">less time spent copying, clicking and re-typing</span>
              </div>
              <a href="/?page=enterprise" className="ai-link">
                Included with VoyCalc Enterprise
                <i className="bi bi-arrow-right ms-2"></i>
              </a>
            </div>
            <div className="ai-chat" aria-hidden="true">
              <div className="ai-chat-header">
                <span className="ai-chat-dot"></span>
                Claude — connected to VoyCalc
              </div>
              <div className="ai-chat-msg user">
                Create an estimate for 60,000 mt grain, Santos → Qingdao on TESS 58, laycan 12–18
                Sep — and add a Cape option.
              </div>
              <div className="ai-chat-msg agent">
                Done — estimate created with both options. Suez: TCE $18,386/day · Cape:
                $17,140/day. Suez wins by $1,246/day.
              </div>
              <div className="ai-chat-msg user">
                Add MV Nordic Falcon to the vessel list from this spec sheet.
              </div>
              <div className="ai-chat-msg agent">
                Vessel added — speeds, consumption and constants stored.
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="features-section">
        <div className="features-container">
          <h2 className="features-title">Why VoyCalc?</h2>
          <div className="features-grid">
            <div className="feature-card">
              <div className="feature-icon">
                <i className="bi bi-speedometer2"></i>
              </div>
              <h3>Fast Estimates</h3>
              <p>Create accurate voyage estimates in minutes by efficient re-use of your own data!</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">
                <i className="bi bi-globe"></i>
              </div>
              <h3>Global Routes</h3>
              <p>Access worldwide port data and calculate optimal routes.</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">
                <i className="bi bi-graph-up"></i>
              </div>
              <h3>Smart Analytics</h3>
              <p>Compare options with a click, and make data-driven decisions.</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">
                <i className="bi bi-cloud-check"></i>
              </div>
              <h3>Cloud Based</h3>
              <p>Access your estimates anywhere, anytime, on any device.</p>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Default;
